import styled from "@emotion/styled"
import React, { useState } from "react"
import { Formik } from "formik"
import * as yup from "yup"
import { Seo } from "gatsby-theme-grape-blog/src/components/seo"
import Layout, { OneColumn, Main } from "../components/layout"
import { Header } from "../components/header"
import PageHero from "../components/page-hero"
import { MdSend } from "react-icons/md"
import { ErrorAlert } from "../components/form/error-alert"
import { Alert } from "../components/basic/alert"
import {
  FormActions,
  PrimarySubmitButton,
  SecureField,
  SecureForm,
  VerificationRow,
  VerificationWidget,
} from "../components/form/secure-form"
import { useTurnstile } from "../hooks/use-turnstile"

const StyledLayout = styled(Layout)`
  h2 {
    font-weight: 500;
    margin-bottom: 1.5rem;
  }
`

const WhiteSection = styled.div`
  background: #fff;
  margin: 0;
  padding: 0;
`

const validationSchema = yup.object({
  EMAIL: yup.string().email("Invalid email").required("Email is required"),
  FNAME: yup.string().max(100, "Too long"),
})

const NewsletterPage = () => {
  const [isSuccess, setSuccess] = useState(false)
  const [isError, setError] = useState(false)
  const [errorMessage, setErrorMessage] = useState(
    "There was an error. Please try again later."
  )
  const { turnstileContainerId, turnstileToken, resetTurnstile } = useTurnstile(
    {
      onVerify: () => {
        setError(false)
      },
      onError: (message) => {
        setErrorMessage(message)
        setError(true)
      },
      onExpired: (message) => {
        setErrorMessage(message)
        setError(true)
      },
    }
  )

  return (
    <StyledLayout>
      <Seo
        title="Evidence Strong Newsletter"
        description="Subscribe to Evidence Strong newsletter to learn what's new in power & strength sports."
      />
      <Header />
      <PageHero title="Newsletter" />
      <OneColumn>
        <Main>
          <h2>
            What's new in power & strength<br/>
            <small>Get it to your inbox</small>
          </h2>
          <p>
            Each month we digest the latest weightlifting research and send
            concise, actionable takeaways. Straight to your inbox.
          </p>

          {isSuccess ? (
            <Alert show type="success">
              <strong>You’re subscribed, thank you!</strong> Look out for the
              next edition in your inbox.
            </Alert>
          ) : (
            <Formik
              initialValues={{ EMAIL: "", FNAME: "", company: "" }}
              validationSchema={validationSchema}
              onSubmit={async (values, { setSubmitting }) => {
                setError(false)
                setErrorMessage("There was an error. Please try again later.")

                try {
                  if (!turnstileToken) {
                    setErrorMessage(
                      "Please complete the verification and try again."
                    )
                    setError(true)
                    return
                  }

                  const resp = await fetch("/subscribe", {
                    method: "POST",
                    headers: { "Content-Type": "application/json" },
                    body: JSON.stringify({
                      email: values.EMAIL,
                      firstName: values.FNAME ?? "",
                      company: values.company ?? "",
                      turnstileToken,
                    }),
                  })

                  let json
                  try {
                    json = await resp.json()
                  } catch (e) {
                    json = null
                  }

                  if (!resp.ok || !json || !json.ok) {
                    const msg =
                      (json && typeof json.error === "string" && json.error) ??
                      "There was an error. Please try again later."
                    setErrorMessage(msg)
                    setError(true)
                    return
                  }

                  setSuccess(true)
                } catch (e) {
                  setError(true)
                } finally {
                  resetTurnstile()
                  setSubmitting(false)
                }
              }}
            >
              {({ isSubmitting, values, handleChange }) => (
                <>
                  <ErrorAlert
                    show={isError}
                    onClose={() => setError(false)}
                    message={errorMessage}
                  />
                  <SecureForm noValidate>
                    <SecureField
                      name="EMAIL"
                      type="email"
                      label="Email Address"
                    />
                    <SecureField name="FNAME" type="text" label="First Name" />
                    <div aria-hidden="true" className="hidden-field">
                      <input
                        type="text"
                        name="company"
                        tabIndex="-1"
                        autoComplete="off"
                        placeholder="Company *"
                        aria-label="Company *"
                        value={values.company}
                        onChange={handleChange}
                      />
                    </div>
                    <VerificationRow>
                      <VerificationWidget>
                        <div id={turnstileContainerId} />
                      </VerificationWidget>
                      <FormActions>
                        <PrimarySubmitButton
                          type="submit"
                          icon={MdSend}
                          disabled={isSubmitting}
                        >
                          {isSubmitting ? "Submitting…" : "Subscribe"}
                        </PrimarySubmitButton>
                      </FormActions>
                    </VerificationRow>
                  </SecureForm>
                </>
              )}
            </Formik>
          )}
        </Main>
      </OneColumn>
      <WhiteSection>
        <OneColumn>
          <h2>
            Instead of scanning the internet for all there is to find, get it
            all to your email.
          </h2>
          <p>
            By signing up, you'll receive a monthly email with the latest
            weightlifting research summaries in an easy-to-digest form. No need
            to spend hours searching the web for the latest studies, we've got
            you covered. Plus, our content is easy to understand and apply in
            weightlifting training.
          </p>
          <p>
            Join <em>What's new in power & strength</em> today!
          </p>
        </OneColumn>
      </WhiteSection>
    </StyledLayout>
  )
}

export default NewsletterPage
